import { useToast } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSsoLogin, type GenericMutationOptions } from '.'
import type { ChangePasswordRequest, LoggedUser, SendResetPasswordLinkRequest, SetPasswordInfo } from '../dtos'
import type { LoginRequest } from '../dtos/Account/LoginRequest'
import type { LoginResponse } from '../dtos/Account/LoginResponse'

const baseUrl = import.meta.env.VITE_BASE_URL
const apiUrl = import.meta.env.VITE_ACCOUNT_API
const url = `${baseUrl}/${apiUrl}`

export const useLogin = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: LoginRequest) => axios.post<LoginResponse>(`${url}/Login`, payload).then(res => res.data),
    onSuccess: (data) => {
      queryClient.setQueriesData({ queryKey: ['isLogged'] }, data.user)
    }
  })
}

const useLogoutMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ returnUrl, isAutomatic }: { returnUrl: string; isAutomatic: boolean }) => axios.post<string | undefined>(`${url}/Logout`, { returnUrl, isAutomatic }).then(res => res.data),
    onSuccess: () => {
      queryClient.setQueriesData<LoggedUser>({ queryKey: ['isLogged'] }, { logged: false })
    }
  })
}
export const useLogout = () => {
  const { mutateAsync } = useLogoutMutation()
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()
  const isLoggingOut = useRef(false)
  const logout = async (returnUrl?: string, isAutomatic?: boolean) => {
    if (isLoading || isLoggingOut.current)
      return

    try {
      setIsLoading(true)
      isLoggingOut.current = true
      const logoutReturnUrl = await mutateAsync({ returnUrl: returnUrl ?? window.location.href, isAutomatic: isAutomatic ?? false })

      if (logoutReturnUrl)
        window.location.href = logoutReturnUrl
      else {
        toast({
          title: 'Byli jste odhlášeni',
          status: 'success',
          duration: 3000
        })

        setIsLoading(false)
      }

    } catch (error) {
      toast({
        title: 'Chyba při odhlašování',
        status: 'error',
        duration: 3000
      })
      setIsLoading(false)
    }
  }

  return { logout, isLoggingOut: isLoading } as const
}

const isLoggedFetch = () => axios.get<LoggedUser>(`${url}/IsLogged`, {
  timeout: 60000
})

export const useIsLogged = (): UseQueryResult<LoggedUser, AxiosError> =>
  useQuery({
    queryKey: ['isLogged'],
    queryFn: () => isLoggedFetch().then(res => res.data),
    retry: false,
    refetchInterval: 1000 * 60 * 5,
  })

export const useChangePassword = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ChangePasswordRequest) => axios.post(`${url}/ChangePassword`, payload),
    onSuccess: () => {
      queryClient.setQueryData<LoggedUser | undefined>(['isLogged'], (loggedUser) => loggedUser ? ({ ...loggedUser, mustChangePassword: false }) : undefined)
    }
  })
}

export const useGetPasswordRules = () => {
  return useQuery({
    queryKey: ['passwordRules'],
    queryFn: () => axios.get<SetPasswordInfo>(`${url}/GetSetPasswordInfo`).then(res => res.data)
  })
}

export const useSendResetPasswordLink = (options?: GenericMutationOptions<SendResetPasswordLinkRequest>) => {
  return useMutation({
    mutationFn: (payload: SendResetPasswordLinkRequest) => axios.post(`${url}/SendResetPasswordLink`, payload),
    ...options,
  })
}

export const useImpersonateMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (asUserName: string) => axios.post<LoggedUser>(`${url}/Impersonate`, { asUserName }).then(res => res.data),
    onSuccess: (data) => {
      queryClient.setQueryData<LoggedUser>(['isLogged'], data)
    }
  })
}

export const useImpersonate = () => {
  const { mutateAsync, isPending: isLoading } = useImpersonateMutation()

  const navigate = useNavigate()
  const impresonate = (asUserName: string) => {
    if (isLoading)
      return Promise.resolve()

    return mutateAsync(asUserName)
      .then(() => {
        navigate('/')
        window.location.reload()
      })
  }

  return { impresonate, isLoading } as const
}

export const useCebiaLogin = (isCebiaLogin?: boolean, returnUrl?: string) => {
  const [isLoading, login] = useSsoLogin('Cebia', returnUrl)
  
  useEffect(() => {
    if (isCebiaLogin && !isLoading)
      login()
  }, [isCebiaLogin, isLoading, login])
}